import { useEffect, useMemo, useRef, useState } from 'react';
import RatingWidget from '../../components/RatingWidget';
import { chunksToSrt, chunksToText } from './format';
import SystemStatus from './SystemStatus';
import { CheckIcon, CopyIcon, DownloadIcon, NewIcon } from './TranscriptionIcons';

function download(name, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function TranscriptionResult({
  audioUrl,
  chunks,
  fileName,
  language,
  onReset,
  onSoundChange = () => undefined,
  soundEnabled = false,
}) {
  const [includeTimestamps, setIncludeTimestamps] = useState(false);
  const [copiedButton, setCopiedButton] = useState(null);
  const copyResetTimeoutRef = useRef(null);
  const text = useMemo(() => chunksToText(chunks, includeTimestamps), [chunks, includeTimestamps]);
  const hasText = useMemo(() => chunks.some((chunk) => String(chunk.text || '').trim()), [chunks]);
  const baseName = (fileName || 'transcripcio').replace(/\.[^.]+$/, '');
  const copy = (button) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedButton(button);
      clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = setTimeout(() => setCopiedButton(null), 2000);
    }, (error) => {
      console.error('Error copying text: ', error);
    });
  };

  useEffect(() => () => clearTimeout(copyResetTimeoutRef.current), []);

  const toolbarCopied = copiedButton === 'toolbar';
  const copyAllCopied = copiedButton === 'all';

  return (
    <div className="transcription-result transcription-stage">
      <SystemStatus soundEnabled={soundEnabled} onSoundChange={onSoundChange}>
        {hasText ? 'Transcripció finalitzada.' : 'Transcripció finalitzada sense text.'}
      </SystemStatus>
      {audioUrl && <audio className="transcription-audio" controls src={audioUrl} />}
      {hasText ? (
        <div className="transcription-output-frame">
          <div className="transcription-output-toolbar">
            <div className="transcription-output-actions">
              <button type="button" aria-label={toolbarCopied ? 'Copiat!' : 'Copia el text'} onClick={() => copy('toolbar')}>
                {toolbarCopied ? <CheckIcon /> : <CopyIcon />} <span aria-live="polite">{toolbarCopied ? 'Copiat!' : 'Copia el text'}</span>
              </button>
              <button type="button" aria-label="Exporta a .srt" onClick={() => download(`${baseName}.srt`, chunksToSrt(chunks), 'text/plain')}><DownloadIcon /> <span>Exporta a .srt</span></button>
            </div>
            <label className="transcription-output-filter"><input type="checkbox" checked={!includeTimestamps} onChange={(event) => setIncludeTimestamps(!event.target.checked)} /> Treure els temps</label>
          </div>
          <textarea aria-label="Transcripció" readOnly value={text} />
        </div>
      ) : (
        <p className="transcription-empty" role="note">
          No s’ha detectat veu en aquest àudio. Comprova que el fragment seleccionat tingui so i torna-ho a provar.
        </p>
      )}
      <div className="transcription-final-actions">
        {hasText && (
          <button type="button" className="transcription-copy-all" aria-label={copyAllCopied ? 'Copiat!' : 'Copia tot el text'} onClick={() => copy('all')}>
            {copyAllCopied ? <CheckIcon size={18} /> : <CopyIcon size={18} />} <span aria-live="polite">{copyAllCopied ? 'Copiat!' : 'Copia tot el text'}</span>
          </button>
        )}
        <button type="button" className="transcription-new" aria-label="Nova transcripció" onClick={onReset}><NewIcon /> <span>Nova transcripció</span></button>
      </div>
      <RatingWidget language={language} />
    </div>
  );
}
